import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";

const PALETTE = {
  primaryRed: "#D84E55",
  accentBlue: "#3A86FF",
  textDark: "#0F172A",
  textLight: "#475569",
  border: "#E5E7EB",
};

const Container = ({ children }) => (
  <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
);

const Badge = ({ children }) => (
  <span className="inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold bg-white">
    {children}
  </span>
);

const SectionCard = ({ id, title, children }) => (
  <section id={id} className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-7 shadow-sm">
    <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{title}</h2>
    <div className="mt-3 text-[15px] leading-7 text-gray-700">{children}</div>
  </section>
);

const PrivacyPolicy = () => {
  const lastUpdated = useMemo(() => {
    return "5 Apr 2026";
  }, []);

  useEffect(() => {
    document.title = "Privacy Policy | Routesbook.lk";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <div
        className="w-full border-b"
        style={{
          borderColor: PALETTE.border,
          background:
            "linear-gradient(135deg, rgba(216,78,85,0.10) 0%, rgba(58,134,255,0.10) 100%)",
        }}
      >
        <Container>
          <div className="py-10 sm:py-12">
            <div className="flex items-center gap-3 text-sm">
              <Link to="/" className="text-blue-700 hover:underline">
                Home
              </Link>
              <span className="text-gray-400">/</span>
              <span className="text-gray-700">Privacy Policy</span>
            </div>

            <div className="mt-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
              <div>
                <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900">
                  Privacy Policy
                </h1>
                <p className="mt-2 text-sm sm:text-base" style={{ color: PALETTE.textLight }}>
                  How Routesbook.lk collects, uses, shares, and protects your information.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <Badge>Last updated: {lastUpdated}</Badge>
              </div>
            </div>
          </div>
        </Container>
      </div>

      <Container>
        {/* Intro note */}
        <div className="mt-8 bg-white border border-gray-200 rounded-2xl p-5 sm:p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-gray-900">Routesbook.lk Privacy Overview</div>
              <p className="mt-1 text-sm text-gray-700">
                Routesbook.lk is operated by <b>NDD IT Solutions</b>, located at <b>Lenaduwa Junction,
                Godagama, Palatuwa, Matara, Sri Lanka</b>. This Privacy Policy explains how we collect,
                use, disclose, and safeguard your information when you use our platform.
              </p>
            </div>
            <Link
              to="/terms"
              className="inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold text-white"
              style={{ backgroundColor: PALETTE.primaryRed }}
            >
              View Terms
            </Link>
          </div>
        </div>

        {/* TOC */}
        <div className="mt-6 bg-white border border-gray-200 rounded-2xl p-5 sm:p-6 shadow-sm">
          <div className="text-sm font-semibold text-gray-900">On this page</div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            {[
              ["info-we-collect", "Information we collect"],
              ["how-we-use", "How we use your information"],
              ["sharing", "Sharing your information"],
              ["payments", "Payments (PayHere)"],
              ["cookies", "Cookies & tracking technologies"],
              ["security", "Security"],
              ["retention", "Data retention"],
              ["rights", "Your rights"],
              ["children", "Children’s privacy"],
              ["changes", "Changes to this policy"],
              ["jurisdiction", "Jurisdiction"],
              ["contact", "Contact us"],
            ].map(([id, label]) => (
              <a key={id} href={`#${id}`} className="hover:underline" style={{ color: PALETTE.accentBlue }}>
                {label}
              </a>
            ))}
          </div>
        </div>

        <div className="mt-6 space-y-5 pb-16">
          <SectionCard id="info-we-collect" title="1) Information we collect">
            <p>
              We may collect the following categories of information when you use Routesbook.lk:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              <li>
                <b>Account details</b> such as your name, email address, phone number, and password
                (stored securely in encrypted or hashed form).
              </li>
              <li>
                <b>Booking details</b> such as selected routes, travel dates, boarding and dropping
                points, seat numbers, and passenger details entered during booking.
              </li>
              <li>
                <b>Payment-related data</b> such as payment reference IDs, transaction status, and
                related reconciliation details received from our payment provider.
              </li>
              <li>
                <b>Technical data</b> such as IP address, device type, browser information, usage logs,
                and activity records to improve reliability, security, and performance.
              </li>
              <li>
                <b>Support communications</b> including messages, inquiries, and feedback you send to us.
              </li>
            </ul>
          </SectionCard>

          <SectionCard id="how-we-use" title="2) How we use your information">
            <ul className="list-disc pl-5 space-y-2">
              <li>To provide and manage ticket booking services.</li>
              <li>To display, confirm, and support your bookings.</li>
              <li>To send booking confirmations, OTPs, service messages, and support replies by email or SMS.</li>
              <li>To process payments, verify transactions, and generate related records.</li>
              <li>To prevent fraud, abuse, suspicious activity, and unauthorized access.</li>
              <li>To improve the platform, including performance, user experience, stability, and reliability.</li>
              <li>To comply with legal, accounting, and regulatory obligations.</li>
            </ul>
          </SectionCard>

          <SectionCard id="sharing" title="3) Sharing your information">
            <p>
              We do not sell your personal data. We may share information only when reasonably necessary
              to operate the service or comply with legal obligations:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>
                <b>Bus operators:</b> relevant booking and passenger details may be shared with the bus
                operator to fulfill your trip and verify seat allocations.
              </li>
              <li>
                <b>Payment processing:</b> with <b>PayHere</b> to complete and verify online payments.
              </li>
              <li>
                <b>Communication providers:</b> with email or SMS service providers to send OTPs,
                booking confirmations, and support messages.
              </li>
              <li>
                <b>Hosting and infrastructure providers:</b> where required to securely operate,
                maintain, and improve the platform.
              </li>
              <li>
                <b>Legal requirements:</b> if required by law, lawful requests, court order, or where
                necessary to protect users, operators, our business, or the platform.
              </li>
            </ul>
          </SectionCard>

          <SectionCard id="payments" title="4) Payments (PayHere)">
            <p>
              When you make an online payment on Routesbook.lk, payment handling is processed through
              <b> PayHere</b>. We receive payment confirmation details such as transaction references,
              payment status, and related verification information for reconciliation, customer support,
              refund handling, and record keeping.
            </p>
            <p className="mt-3">
              Routesbook.lk does <b>not</b> store full card details on its own systems. Payment data
              security is handled by the payment service provider according to their own standards and policies.
            </p>
          </SectionCard>

          <SectionCard id="cookies" title="5) Cookies & tracking technologies">
            <p>
              We may use cookies and similar technologies to improve your experience on the platform.
              These technologies may be used to:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Keep you signed in where applicable.</li>
              <li>Remember preferences and improve usability.</li>
              <li>Support essential platform functionality.</li>
              <li>Measure usage, diagnose issues, and improve performance.</li>
            </ul>
            <p className="mt-3">
              You may control or disable cookies through your browser settings, but some platform features
              may not function properly if certain cookies are disabled.
            </p>
          </SectionCard>

          <SectionCard id="security" title="6) Security">
            <p>
              We take reasonable administrative, technical, and operational measures to help protect
              your information, including:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>HTTPS encryption for data transmitted through supported browsers and secure connections.</li>
              <li>Secure authentication and access controls.</li>
              <li>Password hashing or secure storage practices for account credentials.</li>
              <li>System monitoring, logging, and abuse detection to identify suspicious activity.</li>
              <li>Restricted internal access to data where necessary for operations and support.</li>
            </ul>
            <p className="mt-3">
              However, no method of transmission or storage is completely secure, and we cannot guarantee
              absolute security.
            </p>
          </SectionCard>

          <SectionCard id="retention" title="7) Data retention">
            <p>
              We retain information only for as long as reasonably necessary to provide our services,
              maintain platform integrity, comply with legal and accounting obligations, resolve disputes,
              prevent fraud, and enforce our policies.
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>
                <b>Booking and payment reference data</b> may be retained for record keeping, legal,
                accounting, support, dispute, and refund purposes.
              </li>
              <li>
                <b>Account information</b> may be retained while your account remains active, and for a
                limited period after deletion where required by law or legitimate business needs.
              </li>
              <li>
                <b>Technical logs</b> may be retained for security, troubleshooting, and abuse prevention.
              </li>
            </ul>
          </SectionCard>

          <SectionCard id="rights" title="8) Your rights">
            <p>
              Subject to applicable law, you may have the right to:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Request access to your personal information.</li>
              <li>Request correction of inaccurate or incomplete information.</li>
              <li>Request deletion of your account or certain personal data, subject to legal and accounting requirements.</li>
              <li>Withdraw consent where processing is based on consent.</li>
              <li>Contact us with privacy-related questions, concerns, or complaints.</li>
            </ul>
          </SectionCard>

          <SectionCard id="children" title="9) Children’s privacy">
            <p>
              Routesbook.lk is not intended for use by individuals under the age of 18. We do not
              knowingly collect personal information from children. If we become aware that personal
              information from a child has been submitted to us, we will take reasonable steps to remove it.
            </p>
          </SectionCard>

          <SectionCard id="changes" title="10) Changes to this policy">
            <p>
              We may update this Privacy Policy from time to time to reflect changes to our services,
              business operations, legal requirements, or privacy practices. Any updates will be posted
              on this page with a revised <b>Last updated</b> date.
            </p>
          </SectionCard>

          <SectionCard id="jurisdiction" title="11) Jurisdiction">
            <p>
              This Privacy Policy shall be governed by and interpreted in accordance with the laws of
              <b> Sri Lanka</b>.
            </p>
          </SectionCard>

          <SectionCard id="contact" title="12) Contact us">
            <p>
              If you have any questions, requests, or concerns regarding this Privacy Policy or your data,
              please contact us:
            </p>
            <p className="mt-3">
              Email:{" "}
              <a
                className="font-semibold hover:underline"
                href="mailto:support@routesbook.lk"
                style={{ color: PALETTE.accentBlue }}
              >
                support@routesbook.lk
              </a>
            </p>
            <p className="mt-2">
              Business name: <b>NDD IT Solutions</b>
              <br />
              Address: <b>Lenaduwa Junction, Godagama, Palatuwa, Matara, Sri Lanka</b>
              <br />
              Phone: <b>0767037547</b>
            </p>
          </SectionCard>

          <div className="text-xs text-gray-500">
            This page is provided for general informational purposes and does not constitute legal advice.
          </div>
        </div>
      </Container>
    </div>
  );
};

export default PrivacyPolicy;
